import { useCallback } from "react";
import useAuthStore from "@/store/use-auth-store";
import { useSiteConfigs } from "@/features/ai-model-selection/hooks/use-site-configs";
import { useAgreementsStore } from "../store/agreements-store";

/**
 * Hook for checking user's agreement confirmations against site config versions
 * Opens modal if there are pending agreements to confirm
 */
export const useAgreementsCheck = () => {
  const { user } = useAuthStore();
  const { data: siteConfigData } = useSiteConfigs({ enabled: !!user?._id });
  const { setPendingAgreements } = useAgreementsStore();

  const checkAgreements = useCallback(() => {
    // Guard: user must be logged in
    if (!user?._id) {
      return;
    }

    // Guard: site config must be loaded
    const siteAgreements = siteConfigData?.data?.agreements;
    if (!siteAgreements || siteAgreements.length === 0) {
      return;
    }

    // Get user's confirmed agreements
    const userConfirmedAgreements = user.agreementConfirmations || [];

    // Find pending agreements (not confirmed or version mismatch)
    const pendingAgreements = siteAgreements.filter((siteAgreement) => {
      const userConfirmed = userConfirmedAgreements.find(
        (userAgreement) => userAgreement.agreement.name === siteAgreement.name
      );

      if (!userConfirmed) {
        // User has not confirmed this agreement at all
        return true;
      }

      // Check version mismatch
      const siteVersion = new Date(siteAgreement.version).getTime();
      const userVersion = new Date(userConfirmed.version).getTime();

      if (siteVersion > userVersion) {
        return true;
      }

      return false;
    });

    // Set pending agreements and open modal if needed
    if (pendingAgreements.length > 0) {
      setPendingAgreements(pendingAgreements);
    }
  }, [user, siteConfigData, setPendingAgreements]);

  return {
    checkAgreements,
  };
};
